// CSV row validation + duplicate detection. Pure functions — given a
// parsed CSV row, the resolved mapping, and the cached patient pools,
// returns a per-row status + the resolved patient object. No DOM, no
// async, no bridge fetches. flow.js's preview step wires the UI around
// the array of row states this returns.

// Resolved patient shape (matches bulk_create_specialist_patients's
// per-row input per the SC1 RPC contract):
//   { name, dob, pronouns, parent_name_hint, parent_email_hint, notes }

// Per-row status discriminator:
//   'ok'        — passes all checks, no adjustments needed
//   'flagged'   — passes BUT some values adjusted (name truncated,
//                 pronouns nulled, email nulled). Still imports.
//                 .warnings carries the human-readable details.
//   'duplicate' — passes BUT matches an existing patient (pre-parent
//                 or connected child). User decides Skip / Create anyway.
//                 .dupes carries the matched source labels.
//   'invalid'   — fails minimum validity: no name resolvable, OR dob
//                 present but unparseable. Excluded from import.
//                 .reason carries why.
//
// A row can be both flagged AND duplicate; 'duplicate' wins for the
// badge because it's the more user-actionable surface, but the warnings
// still render in the row's detail line.

const T = (k, p) => window.HUD?.T?.(k, p) || k;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Pronoun synonyms → normalized he/she/they. Anything outside this map
// becomes null (with a warning), per spec §7 "unknown pronouns → null".
const PRONOUN_NORMALIZE = {
  he: 'he', him: 'he', m: 'he', male: 'he', boy: 'he',
  she: 'she', her: 'she', f: 'she', female: 'she', girl: 'she',
  they: 'they', them: 'they', t: 'they', nonbinary: 'they', 'non-binary': 'they', nb: 'they',
};

const NAME_MAX = 200;

// Parse a date string into ISO YYYY-MM-DD, or null if unparseable.
// Strategy per spec §7: ISO 8601 first (YYYY-MM-DD or YYYY/MM/DD),
// then EU fallback (DD/MM/YYYY or DD-MM-YYYY).
export function parseDob(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;

  // ISO: YYYY-MM-DD or YYYY/MM/DD
  let m = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (m) {
    const y = m[1], mo = m[2].padStart(2, '0'), d = m[3].padStart(2, '0');
    return _isValidYmd(y, mo, d) ? y + '-' + mo + '-' + d : null;
  }

  // EU: DD/MM/YYYY or DD-MM-YYYY
  m = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (m) {
    const d = m[1].padStart(2, '0'), mo = m[2].padStart(2, '0'), y = m[3];
    return _isValidYmd(y, mo, d) ? y + '-' + mo + '-' + d : null;
  }

  return null;
}

function _isValidYmd(yyyy, mm, dd) {
  const y = +yyyy, m = +mm, d = +dd;
  if (y < 1900 || y > 2100) return false;
  if (m < 1 || m > 12) return false;
  if (d < 1 || d > 31) return false;
  // Round-trip via UTC Date to catch invalid days-in-month (e.g., Feb 30).
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

function _trim(v) {
  return v == null ? '' : String(v).trim();
}

// Find the first CSV header that maps to `target` in the user's mapping,
// then return that column's trimmed value for the given row. '' if no
// column maps to target or the cell is empty.
function _resolveCol(row, mapping, target) {
  for (const csvHeader in mapping) {
    if (mapping[csvHeader] === target) return _trim(row[csvHeader]);
  }
  return '';
}

// Resolve the name field per the concatenation contract documented in
// mapping.js: full `name` wins if mapped; else join first_name + last_name
// space-separated, dropping empty parts. SC5's gate guarantees at least
// one of the three is mapped (or this would have been blocked there).
function _resolveName(row, mapping) {
  const full = _resolveCol(row, mapping, 'name');
  if (full) return full;
  const first = _resolveCol(row, mapping, 'first_name');
  const last = _resolveCol(row, mapping, 'last_name');
  return [first, last].filter(p => p.length).join(' ');
}

// Turn one parsed CSV row + the user's mapping into a resolved patient
// object + a validity verdict. Does NOT do dedup — that needs the DB
// caches and runs as a separate pass below.
export function resolveRow(row, mapping) {
  const warnings = [];
  let invalid = false;
  let reason = '';

  // Name
  let name = _resolveName(row, mapping);
  if (name.length > NAME_MAX) {
    name = name.slice(0, NAME_MAX);
    warnings.push(T('csv_warn_name_truncated', { max: NAME_MAX }));
  }
  if (!name) {
    invalid = true;
    reason = T('csv_reason_no_name');
  }

  // DOB
  const dobRaw = _resolveCol(row, mapping, 'dob');
  let dob = null;
  if (dobRaw) {
    dob = parseDob(dobRaw);
    if (!dob) {
      invalid = true;
      reason = reason || T('csv_reason_bad_date', { date: dobRaw });
    }
  }

  // Pronouns — normalize variants, null on unknown
  const pronounsRaw = _resolveCol(row, mapping, 'pronouns').toLowerCase();
  let pronouns = null;
  if (pronounsRaw) {
    pronouns = PRONOUN_NORMALIZE[pronounsRaw] || null;
    if (!pronouns) warnings.push(T('csv_warn_unknown_pronouns', { val: pronounsRaw }));
  }

  // Parent email — null on bad shape
  const emailRaw = _resolveCol(row, mapping, 'parent_email_hint');
  let parent_email_hint = null;
  if (emailRaw) {
    parent_email_hint = EMAIL_RE.test(emailRaw) ? emailRaw : null;
    if (!parent_email_hint) warnings.push(T('csv_warn_bad_email', { val: emailRaw }));
  }

  // Pass-throughs — empty string normalizes to null so the RPC stores NULL
  const parent_name_hint = _resolveCol(row, mapping, 'parent_name_hint') || null;
  const notes = _resolveCol(row, mapping, 'notes') || null;

  const patient = { name, dob, pronouns, parent_name_hint, parent_email_hint, notes };

  let status;
  if (invalid) status = 'invalid';
  else if (warnings.length > 0) status = 'flagged';
  else status = 'ok';

  return { patient, status, reason, warnings };
}

// Detect duplicates of the resolved patient against the cached patient
// pools. Case-insensitive name match against both populations; if the row
// has parent_email_hint, also match on email vs specialist_patients (the
// only pool that stores parent_email_hint — DB.children links to a user
// row via parent_id, not a denormalized email field).
//
// Pre-parent specialist_patients are dedup-eligible only when status is
// 'active' or 'archived'. 'linked' rows were merged into children, so
// they'd double-count if both pools matched the same person.
//
// Returns an array of match descriptors (possibly empty). One entry per
// source (one pre-parent + one child max), each with { source, id, name }
// for display.
export function detectDuplicates(patient, specialistPatients, children) {
  if (!patient.name) return [];
  const matches = [];
  const nameLower = patient.name.toLowerCase();
  const emailLower = patient.parent_email_hint ? patient.parent_email_hint.toLowerCase() : '';

  for (const sp of (specialistPatients || [])) {
    if (sp.status !== 'active' && sp.status !== 'archived') continue;
    const spName = (sp.name || '').toLowerCase();
    const spEmail = (sp.parent_email_hint || '').toLowerCase();
    const nameMatch = spName && spName === nameLower;
    const emailMatch = emailLower && spEmail && spEmail === emailLower;
    if (nameMatch || emailMatch) {
      matches.push({ source: T('csv_src_preparent'), id: sp.id, name: sp.name });
      break; // one label per source is enough; user just needs to know it matched
    }
  }

  for (const ch of (children || [])) {
    const chName = (ch.name || '').toLowerCase();
    if (chName && chName === nameLower) {
      matches.push({ source: T('csv_src_child'), id: ch.id, name: ch.name });
      break;
    }
  }

  return matches;
}

// Run the full pipeline over all parsed rows. Returns an array of row
// states, one per input row, in original order. Each entry:
//   {
//     rowIndex,       // 0-based position in parsedRows
//     patient,        // resolved {name, dob, pronouns, parent_name_hint,
//                     //           parent_email_hint, notes}
//     status,         // 'ok' | 'flagged' | 'duplicate' | 'invalid'
//     reason,         // why invalid (empty otherwise)
//     warnings,       // [string] of flagged adjustments
//     dupes,          // [{source, id, name}] — non-empty when status='duplicate'
//     decision,       // 'create' | 'skip' | 'create-anyway' | 'exclude'
//                     //   set by flow.js's UI toggles for duplicate rows;
//                     //   defaults: invalid→'exclude', duplicate→'skip',
//                     //   ok/flagged→'create'
//   }
export function buildPreview(parsedRows, mapping, specialistPatients, children) {
  return (parsedRows || []).map((row, idx) => {
    const { patient, status, reason, warnings } = resolveRow(row, mapping);

    let dupes = [];
    if (status !== 'invalid') {
      dupes = detectDuplicates(patient, specialistPatients, children);
    }

    // 'duplicate' supersedes 'flagged' for the badge — more user-actionable.
    // Warnings still render via the row-detail line so adjustments stay visible.
    let finalStatus = status;
    if (dupes.length > 0 && status !== 'invalid') finalStatus = 'duplicate';

    let decision;
    if (finalStatus === 'invalid') decision = 'exclude';
    else if (finalStatus === 'duplicate') decision = 'skip';
    else decision = 'create';

    return { rowIndex: idx, patient, status: finalStatus, reason, warnings, dupes, decision };
  });
}
